<template>
  <aside
    class="cai-history"
    aria-label="Your saved chats"
  >
    <div class="cai-history__top">
      <button
        class="cai-history__new"
        @click="$emit('new')"
      >
        + New chat
      </button>
      <button
        class="cai-history__close"
        title="Hide your chats"
        @click="$emit('close')"
      >
        ✕
      </button>
    </div>

    <div class="cai-history__title">
      Your chats
    </div>

    <div
      v-if="loading && !chats.length"
      class="cai-history__note"
    >
      Loading…
    </div>
    <div
      v-else-if="error"
      class="cai-history__note"
    >
      Your chats can't be shown right now.
    </div>
    <div
      v-else-if="!chats.length"
      class="cai-history__note"
    >
      No saved chats yet.
    </div>

    <ul class="cai-history__list">
      <li
        v-for="c in chats"
        :key="c.id"
        class="cai-history__item"
        :class="{ 'cai-history__item--active': c.id === activeId }"
      >
        <template v-if="editingId === c.id">
          <input
            ref="renameInput"
            v-model="draftTitle"
            class="cai-history__rename"
            maxlength="200"
            @keyup.enter="saveRename(c)"
            @keyup.esc="editingId = null"
          >
          <div class="cai-history__actions cai-history__actions--visible">
            <button
              title="Save the new name"
              @click="saveRename(c)"
            >
              Save
            </button>
            <button
              title="Cancel"
              @click="editingId = null"
            >
              Cancel
            </button>
          </div>
        </template>
        <template v-else>
          <button
            class="cai-history__open"
            :title="c.title || 'Untitled chat'"
            @click="$emit('open', c.id)"
          >
            <span class="cai-history__name">{{ c.title || 'Untitled chat' }}</span>
            <span class="cai-history__meta">{{ shortDate(c.updatedAt) }} · {{ agentLabel(c.contextKey) }}</span>
          </button>
          <div class="cai-history__actions">
            <button
              title="Rename this chat"
              @click="startRename(c)"
            >
              Rename
            </button>
            <button
              title="Delete this chat"
              @click="confirmDelete(c)"
            >
              Delete
            </button>
          </div>
        </template>
      </li>
    </ul>

    <div class="cai-history__consent">
      <label class="cai-history__switch">
        <input
          type="checkbox"
          :checked="consent === true"
          :disabled="consent === null"
          @change="$emit('consent', $event.target.checked)"
        >
        Save my chats
      </label>
      <div
        v-if="consent === false"
        class="cai-history__note cai-history__note--small"
      >
        Saving is off. New messages are not saved.
      </div>
    </div>
  </aside>
</template>

<script>
export default {
  props: {
    chats: {
      type: Array,
      required: true
    },
    activeId: {
      type: String,
      default: null
    },
    loading: {
      type: Boolean,
      default: false
    },
    error: {
      type: Boolean,
      default: false
    },
    // true = saving on, false = off, null = not known yet
    consent: {
      type: Boolean,
      default: null
    },
    // [{ id, name }] from the chatbot's /contexts, to show the agent's name
    contexts: {
      type: Array,
      default: () => []
    }
  },

  data () {
    return {
      editingId: null,
      draftTitle: ''
    }
  },

  methods: {
    shortDate (iso) {
      if (!iso) return ''
      const d = new Date(iso)
      if (isNaN(d.getTime())) return ''
      return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' })
    },

    agentLabel (key) {
      const match = this.contexts.find(c => String(c.id).toLowerCase().replace(/[^a-z0-9_]/g, '_') === key)
      const name = match ? match.name : (key || 'general')
      return String(name).replace(/\s*\([^)]*\)\s*$/, '')
    },

    startRename (chat) {
      this.editingId = chat.id
      this.draftTitle = chat.title || ''
      this.$nextTick(() => {
        const el = Array.isArray(this.$refs.renameInput) ? this.$refs.renameInput[0] : this.$refs.renameInput
        if (el) el.focus()
      })
    },

    saveRename (chat) {
      const title = this.draftTitle.trim()
      this.editingId = null
      if (title && title !== chat.title) this.$emit('rename', { id: chat.id, title })
    },

    confirmDelete (chat) {
      const name = chat.title || 'this chat'
      if (window.confirm(`Delete "${name}" for good? This can't be undone.`)) {
        this.$emit('delete', chat.id)
      }
    }
  }
}
</script>
